import { Briefcase, Mail, Sparkles } from "lucide-react";
import { GoogleBrandIcon } from "./icons/GoogleBrandIcon";
import { MicrosoftBrandIcon } from "./icons/MicrosoftBrandIcon";
import { ProviderBrandIcon } from "./icons/ProviderBrandIcon";
import type { AccountIconKey } from "./accountIconOptions";
import { cn } from "@/lib/cn";

type Props = {
  iconKey: AccountIconKey;
  providerId: string;
  className?: string;
  /** Plein cercle (avatar) ou icône seule (grille de sélection). */
  variant?: "avatar" | "plain";
};

const GENERIC_STYLES: Record<
  Exclude<AccountIconKey, "provider" | "google" | "microsoft">,
  { bg: string; fg: string }
> = {
  mail: { bg: "bg-sky-100", fg: "text-sky-600" },
  briefcase: { bg: "bg-amber-100", fg: "text-amber-700" },
  sparkles: { bg: "bg-violet-100", fg: "text-violet-600" },
};

export function AccountPresetIcon({
  iconKey,
  providerId,
  className,
  variant = "plain",
}: Props) {
  const isAvatar = variant === "avatar";

  if (iconKey === "provider") {
    if (isAvatar) {
      return (
        <span
          className={cn(
            "inline-flex h-full w-full items-center justify-center rounded-full bg-[var(--mail-surface-hover)]",
            className
          )}
        >
          <ProviderBrandIcon providerId={providerId} className="h-[55%] w-[55%]" />
        </span>
      );
    }
    return <ProviderBrandIcon providerId={providerId} className={className} />;
  }

  if (iconKey === "google") {
    return <GoogleBrandIcon className={className} />;
  }

  if (iconKey === "microsoft") {
    return <MicrosoftBrandIcon className={className} />;
  }

  const style = GENERIC_STYLES[iconKey];
  const Icon = iconKey === "mail" ? Mail : iconKey === "briefcase" ? Briefcase : Sparkles;

  if (isAvatar) {
    return (
      <span
        className={cn(
          "inline-flex h-full w-full items-center justify-center rounded-full",
          style.bg,
          className
        )}
      >
        <Icon className={cn("h-[50%] w-[50%]", style.fg)} strokeWidth={2} aria-hidden />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-lg",
        style.bg,
        className
      )}
    >
      <Icon className={cn("h-[62%] w-[62%]", style.fg)} strokeWidth={2} aria-hidden />
    </span>
  );
}
